import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { PlaylistVideo, PrimeVideoSettings } from '@/store/playlist/types';
import { PrimeVideoCard } from './PrimeVideoCard';
import { PrimeVideoModal } from './PrimeVideoModal';
import { TemplateDarkMembersFooter } from './TemplateDarkMembersFooter';
import { cn } from '@/lib/utils';

interface TemplatePrimeVideoPreviewProps {
  videos: PlaylistVideo[];
  settings?: PrimeVideoSettings;
  playlistName?: string;
  publicTitle?: string;
  description?: string;
  favicon?: string;
}

const defaultSettings: PrimeVideoSettings = {
  accentColor: '#2D7BFF',
  autoPlayNext: false,
};

export function TemplatePrimeVideoPreview({
  videos,
  settings = defaultSettings,
  playlistName,
  publicTitle,
  description,
  favicon,
}: TemplatePrimeVideoPreviewProps) {
  useLanguage();
  const [selectedVideo, setSelectedVideo] = useState<PlaylistVideo | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  const accentColor = settings.accentColor || '#2D7BFF';
  const heroImage = settings.heroImage;
  
  // Pagination: 12 videos per page (6 columns x 2 rows)
  const videosPerPage = 12;
  const totalPages = Math.ceil(videos.length / videosPerPage);
  const startIndex = currentPage * videosPerPage;
  const endIndex = startIndex + videosPerPage;
  const currentVideos = videos.slice(startIndex, endIndex);

  const handleVideoClick = (video: PlaylistVideo) => {
    setSelectedVideo(video);
  };

  const handleCloseModal = () => {
    setSelectedVideo(null);
  };

  return (
    <div data-theme-isolated className="min-h-screen bg-[#0B0B0B] flex flex-col">
      {/* Hero Banner - 1920x1080 */}
      {heroImage && (
        <section className="w-full relative">
          <div 
            className="w-full bg-[#141414]"
            style={{
              height: '550px',
            }}
          >
            <img
              src={heroImage}
              alt={translateInterfaceText("Hero Banner")}
              className="w-full h-full object-cover object-center"
              style={{
                imageRendering: 'auto',
              }}
            />
            
            {/* Gradient shadow at bottom */}
            <div 
              className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, transparent 0%, rgba(11, 11, 11, 0.6) 50%, #0B0B0B 100%)'
              }}
            />
          </div>
        </section>
      )}

      {/* Main Content - Course Classes */}
      <main className="flex-1 px-8 py-12">
        <div className="max-w-[1600px] mx-auto">
          {/* Section Title */}
          <div className="mb-8">
            <h2 className="text-white text-3xl font-bold">
              {translateInterfaceText("Aulas do Curso")}</h2>
            <p className="text-gray-400 text-base mt-2">
              {videos.length} {videos.length === 1 ? translateInterfaceText("aula disponível") : translateInterfaceText("aulas disponíveis")}
            </p>
          </div>

          {/* Grid: 6 columns x 2 rows */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
            {currentVideos.map((video, index) => (
              <PrimeVideoCard
                key={video.id}
                video={video}
                index={startIndex + index}
                accentColor={accentColor}
                onClick={() => handleVideoClick(video)}
              />
            ))}
          </div>

          {/* Pagination Dots */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPage(index)}
                  className={cn(
                    'w-2.5 h-2.5 rounded-full transition-all duration-200',
                    currentPage === index
                      ? 'w-8'
                      : 'hover:opacity-80'
                  )}
                  style={{
                    backgroundColor: currentPage === index ? accentColor : 'rgba(255,255,255,0.3)',
                  }}
                  aria-label={translateInterfaceText("Página {value0}", { value0: (index + 1) })}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <TemplateDarkMembersFooter
        showTerms={true}
        showPrivacy={true}
      />

      {/* Video Modal */}
      {selectedVideo && (
        <PrimeVideoModal
          isOpen={!!selectedVideo}
          onClose={handleCloseModal}
          currentVideo={selectedVideo}
          allVideos={videos}
          accentColor={accentColor}
          playlistLogo={favicon}
          autoPlayNext={settings.autoPlayNext}
        />
      )}
    </div>
  );
}
